import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Download, Printer, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { cn, formatPrice, orderStatuses, itemMeasurements } from '@/lib/utils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// صور uploads بنجيبها عن طريق /api/media (فيها CORS) عشان تظهر في الـ PDF
const mediaUrl = (url) => {
  if (!url) return null;
  const i = url.indexOf('/uploads/');
  return i === -1 ? url : `${API}/media/${url.slice(i + '/uploads/'.length)}`;
};

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-800 ring-amber-200',
  confirmed: 'bg-sky-50 text-sky-800 ring-sky-200',
  processing: 'bg-indigo-50 text-indigo-800 ring-indigo-200',
  shipped: 'bg-violet-50 text-violet-800 ring-violet-200',
  delivered: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  cancelled: 'bg-rose-50 text-rose-800 ring-rose-200',
};
const STEPS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];

const fmtDate = (d) =>
  new Date(d).toLocaleString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/**
 * فاتورة الطلب: عرض + طباعة + تحميل PDF (بيترسم من نفس الصفحة، فالعربي بيطلع سليم).
 * ?download=1 بيحمّل الـ PDF تلقائياً أول ما الفاتورة تجهز.
 */
const Invoice = () => {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const { user, token, loading: authLoading } = useAuth();
  const site = useSettings();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const sheet = useRef(null);
  const autoDownloaded = useRef(false);

  useEffect(() => {
    if (authLoading || !token) return;
    axios
      .get(`${API}/orders/${orderNumber}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setOrder(res.data?.data))
      .catch(() => setError(true));
  }, [orderNumber, token, authLoading]);

  useEffect(() => {
    if (order) document.title = `فاتورة ${order.order_number} — ${site.site_name}`;
  }, [order, site.site_name]);

  const downloadPdf = async () => {
    if (!sheet.current) return;
    setBusy(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
      const canvas = await html2canvas(sheet.current, {
        scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
        // html2canvas بينزّل النص العربي كام بكسل جوّه الشارات — بنعوّضه في النسخة المصوّرة بس
        onclone: (doc) => doc.querySelector('.invoice-sheet')?.classList.add('is-pdf'),
      });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;
      const img = canvas.toDataURL('image/jpeg', 0.95);

      // فاتورة طويلة: نقسمها على كذا صفحة
      let offset = 0;
      pdf.addImage(img, 'JPEG', 0, offset, pageW, imgH);
      while (imgH + offset > pageH + 1) {
        offset -= pageH;
        pdf.addPage();
        pdf.addImage(img, 'JPEG', 0, offset, pageW, imgH);
      }
      pdf.save(`فاتورة-${order.order_number}.pdf`);
      toast.success('تم تحميل الفاتورة');
    } catch (e) {
      console.error(e);
      toast.error('تعذّر إنشاء ملف PDF، جرّبي الطباعة واختاري "حفظ كـ PDF"');
    } finally {
      setBusy(false);
    }
  };

  // ?download=1: تحميل تلقائي بعد ما الصور تحمل
  useEffect(() => {
    if (!order || autoDownloaded.current || params.get('download') !== '1') return;
    autoDownloaded.current = true;
    const imgs = [...(sheet.current?.querySelectorAll('img') || [])];
    Promise.all(imgs.map((img) => (img.complete ? null : new Promise((r) => { img.onload = img.onerror = r; })))).then(() =>
      setTimeout(downloadPdf, 300)
    );
  }, [order]);

  if (!authLoading && !user) {
    return (
      <div className="py-24 text-center font-cairo">
        <p className="text-gray-600 mb-4">سجّلي الدخول لعرض الفاتورة</p>
        <Link to="/login" className="text-burgundy-500 font-bold hover:underline">تسجيل الدخول</Link>
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-24 text-center font-cairo">
        <p className="text-gray-600 mb-4">لم نجد هذه الفاتورة</p>
        <Link to="/account?tab=orders" className="text-burgundy-500 font-bold hover:underline">طلباتي</Link>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  const address = order.shipping_address || {};
  const cancelled = order.status === 'cancelled';
  const stepIndex = STEPS.indexOf(order.status);

  return (
    <div className="invoice-page bg-gray-100 min-h-screen py-6 md:py-10">
      {/* شريط الأدوات (مش بيظهر في الطباعة/الـ PDF) */}
      <div className="invoice-toolbar max-w-[820px] mx-auto px-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to="/account?tab=orders" className="inline-flex items-center gap-1.5 text-sm font-cairo font-semibold text-gray-600 hover:text-burgundy-500">
          <ArrowRight className="w-4 h-4" /> طلباتي
        </Link>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-gray-200 text-sm font-cairo font-semibold hover:border-burgundy-500 hover:text-burgundy-500 transition"
          >
            <Printer className="w-4 h-4" /> طباعة
          </button>
          <button
            type="button"
            onClick={downloadPdf}
            disabled={busy}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-burgundy-500 hover:bg-burgundy-600 text-white text-sm font-cairo font-semibold shadow-sm transition disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {busy ? 'جاري التجهيز...' : 'تحميل PDF'}
          </button>
        </div>
      </div>

      {/* الفاتورة (عرض A4) */}
      <div className="overflow-x-auto px-4">
        <article ref={sheet} dir="rtl" className="invoice-sheet relative mx-auto w-[794px] bg-white text-brand-black shadow-xl font-cairo overflow-hidden">
          {cancelled && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10">
              <span className="rotate-[-24deg] border-[6px] border-rose-500/40 text-rose-500/40 rounded-3xl px-10 py-2 text-7xl font-tajawal font-extrabold">ملغي</span>
            </div>
          )}

          {/* الهيدر */}
          <header className="relative bg-[#722F37] text-white px-12 pt-10 pb-8 overflow-hidden">
            <div className="absolute inset-0 opacity-50" style={{ backgroundImage: 'radial-gradient(rgba(244,213,141,0.25) 1px, transparent 1.4px)', backgroundSize: '18px 18px' }} />
            <div className="absolute -left-16 -top-16 w-56 h-56 rounded-full border-[28px] border-[#F4D58D]/10" />
            <div className="relative flex items-start justify-between gap-6">
              <div>
                {site.logo_url ? (
                  <img src={mediaUrl(site.logo_url)} crossOrigin="anonymous" alt={site.site_name} className="h-14 w-auto object-contain bg-white rounded-lg p-1.5" />
                ) : (
                  <p className="font-tajawal font-extrabold text-4xl text-[#F4D58D]">{site.site_name}</p>
                )}
                {site.phone && <p className="mt-3 text-sm text-white/80" dir="ltr" style={{ textAlign: 'right' }}>{site.phone}</p>}
              </div>
              <div className="text-left">
                <p className="font-tajawal font-extrabold text-4xl">فاتورة</p>
                <p className="mt-1 font-mono text-lg text-[#F4D58D]" dir="ltr">{order.order_number}</p>
                <p className="mt-1 text-xs text-white/70">{fmtDate(order.created_at)}</p>
              </div>
            </div>
          </header>

          {/* حالة الطلب الآن */}
          <section className="px-12 py-6 border-b border-gray-100 bg-[#fbf7f4]">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">حالة الطلب الآن</span>
                <span className={cn('inline-flex items-center h-8 px-4 rounded-full text-sm font-bold ring-1', STATUS_STYLE[order.status])}>
                  <span className="pdf-lift">{orderStatuses[order.status] || order.status}</span>
                </span>
              </div>
              <span className="text-sm text-gray-500">الدفع: <strong className="text-brand-black">عند الاستلام</strong></span>
            </div>
            {!cancelled && (
              <div className="mt-5 flex items-center">
                {STEPS.map((s, i) => (
                  <div key={s} className={cn('flex items-center', i < STEPS.length - 1 && 'flex-1')}>
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={cn('w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold',
                        i <= stepIndex ? 'bg-[#722F37] text-[#F4D58D]' : 'bg-white text-gray-300 ring-1 ring-gray-200')}>
                        {i < stepIndex ? '✓' : i + 1}
                      </span>
                      <span className={cn('text-[11px] whitespace-nowrap', i === stepIndex ? 'font-bold text-[#722F37]' : i < stepIndex ? 'text-gray-700' : 'text-gray-400')}>
                        {orderStatuses[s]}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && <div className={cn('flex-1 h-0.5 mx-2 -mt-5', i < stepIndex ? 'bg-[#722F37]' : 'bg-gray-200')} />}
                  </div>
                ))}
              </div>
            )}
            {cancelled && order.cancel_reason && <p className="mt-3 text-sm text-rose-700">سبب الإلغاء: {order.cancel_reason}</p>}
          </section>

          {/* العميل والعنوان */}
          <section className="grid grid-cols-2 gap-8 px-12 py-7">
            <div>
              <p className="text-xs font-bold text-[#722F37] mb-2">فاتورة إلى</p>
              <p className="font-bold text-lg">{address.name || user?.name}</p>
              {user?.email && <p className="text-sm text-gray-600" dir="ltr" style={{ textAlign: 'right' }}>{user.email}</p>}
              {address.phone && <p className="text-sm text-gray-600" dir="ltr" style={{ textAlign: 'right' }}>{address.phone}</p>}
            </div>
            <div>
              <p className="text-xs font-bold text-[#722F37] mb-2">عنوان التوصيل</p>
              <p className="font-semibold">{[address.city, address.street].filter(Boolean).join('، ')}</p>
              {address.building && <p className="text-sm text-gray-600">{address.building}</p>}
              {address.details && <p className="text-sm text-gray-500">{address.details}</p>}
            </div>
          </section>

          {/* المنتجات */}
          <section className="px-12">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#722F37] text-white">
                  <th className="py-3 px-3 text-right font-semibold rounded-r-lg w-10">#</th>
                  <th className="py-3 px-3 text-right font-semibold">المنتج</th>
                  <th className="py-3 px-3 text-center font-semibold">سعر القطعة</th>
                  <th className="py-3 px-3 text-center font-semibold">الكمية</th>
                  <th className="py-3 px-3 text-left font-semibold rounded-l-lg">الإجمالي</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, i) => (
                  <tr key={item.id} className="border-b border-gray-100 align-middle">
                    <td className="py-4 px-3 text-gray-400 font-bold">{i + 1}</td>
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-3">
                        {item.image_url && (
                          <img src={mediaUrl(item.image_url)} crossOrigin="anonymous" alt="" className="w-11 h-12 rounded-md object-cover bg-gray-100" />
                        )}
                        <div>
                          <p className="font-bold">{item.name_ar}</p>
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {[...(item.options || []), ...itemMeasurements(item)].map((o) => (
                              <span key={o.name} className="inline-flex items-center gap-1 rounded-full bg-gray-50 ring-1 ring-gray-200 px-2 py-0.5 text-[11px] text-gray-700">
                                {o.hex && <span className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10" style={{ backgroundColor: o.hex }} />}
                                {!o.hex && <span className="pdf-lift text-gray-400">{o.name}:</span>}
                                <span className="pdf-lift">{o.value}</span>
                              </span>
                            ))}
                            {!item.options?.length && item.options_label && <span className="text-[11px] text-gray-500">{item.options_label}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-center whitespace-nowrap">{formatPrice(item.unit_price)}</td>
                    <td className="py-4 px-3 text-center font-semibold">{item.quantity}</td>
                    <td className="py-4 px-3 text-left font-bold whitespace-nowrap">{formatPrice(item.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* الإجمالي */}
          <section className="px-12 py-7 flex items-start justify-between gap-8">
            <div className="text-sm text-gray-600 max-w-xs space-y-2 pt-1">
              {order.preview_service_requested && (
                <p className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 text-amber-800 px-3 py-1.5 text-xs font-semibold"><span className="pdf-lift">👁 خدمة المعاينة قبل الدفع</span></p>
              )}
              {order.notes && (
                <div>
                  <p className="text-xs font-bold text-[#722F37] mb-1">ملاحظات</p>
                  <p>{order.notes}</p>
                </div>
              )}
            </div>
            <div className="w-72 shrink-0 rounded-2xl bg-[#fbf7f4] p-5 space-y-2.5 text-sm">
              <div className="flex justify-between text-gray-600"><span>المجموع الفرعي</span><span>{formatPrice(order.subtotal)}</span></div>
              <div className="flex justify-between text-gray-600"><span>رسوم التوصيل</span><span>{formatPrice(order.delivery_fee)}</span></div>
              <div className="flex justify-between items-center pt-3 mt-1 border-t border-[#722F37]/15">
                <span className="font-tajawal font-bold text-base">الإجمالي</span>
                <span className="font-tajawal font-extrabold text-2xl text-[#722F37]">{formatPrice(order.total)}</span>
              </div>
              <p className="text-[11px] text-gray-500 text-center pt-1">{cancelled ? 'طلب ملغي — لا يوجد مبلغ مستحق' : 'المبلغ المستحق عند الاستلام'}</p>
            </div>
          </section>

          {/* الفوتر */}
          <footer className="px-12 py-6 bg-[#722F37]/[0.04] border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span>شكراً لتسوقك من <strong className="text-[#722F37]">{site.site_name}</strong> ♡</span>
            <span>فاتورة إلكترونية · {new Date().toLocaleDateString('ar-EG')}</span>
          </footer>
        </article>
      </div>
    </div>
  );
};

export default Invoice;
